import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Linkedin, Github, GitlabIcon as Gitlab, ArrowRight } from 'lucide-react';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

function TeamPage() {
  const founders = [
    {
      name: 'Johan',
      initial: 'J',
      role: 'Co-Founder',
      color: 'blue',
      bio: 'Erfaren backend-udvikler med stærkt fokus på systemarkitektur, cloud-infrastruktur og driftsstabilitet. Johan har mange års praktisk programmeringserfaring og specialiserer sig i at bygge robuste, skalerbare systemer, der kører pålideligt i produktion.',
      socials: [
        {
          icon: Linkedin,
          url: 'https://www.linkedin.com/in/johan-milter-jakobsen-41b4b6198/',
          label: 'LinkedIn'
        },
        {
          icon: Gitlab,
          url: 'https://gitlab.com/m1l73r',
          label: 'GitLab'
        }
      ]
    },
    {
      name: 'Magnus',
      initial: 'M',
      role: 'Co-Founder',
      color: 'cyan',
      bio: 'Strategisk forretningsprofil med stærkt fokus på virksomhedsstrategi, planlægning, kommunikation og markedspositionering. Magnus bygger bro mellem design og funktionalitet.',
      socials: [
        {
          icon: Linkedin,
          url: 'https://www.linkedin.com/in/magnus-nielsen-falck/',
          label: 'LinkedIn'
        }
      ]
    },
    {
      name: 'Sebastian',
      initial: 'S',
      role: 'Co-Founder',
      color: 'purple',
      bio: 'Hybrid teknisk profil, der kombinerer softwareudvikling med taktisk eksekvering. Sebastian har erfaring inden for både programmering og koordinering, og han omsætter strategiske idéer til praktiske, køreklare løsninger.',
      socials: [
        {
          icon: Linkedin,
          url: 'https://www.linkedin.com/in/sebastian-nielsen-9b0a25233/',
          label: 'LinkedIn'
        },
        {
          icon: Github,
          url: 'https://github.com/Sebdenmark',
          label: 'GitHub'
        }
      ]
    }
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="section" style={{ paddingTop: 'calc(var(--header-height) + var(--space-16))' }}>
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="section__tag">Teamet</span>
            <h1 className="section__title">Mød stifterne</h1>
            <p className="section__description">
              Tre passionerede udviklere forenet af en fælles vision: at gøre teknologi til en fordel for jeres virksomhed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="section section--alt">
        <div className="container">
          <div className="team-grid">
            {founders.map((founder, index) => (
              <motion.article
                key={founder.name}
                className="team-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="team-card__header">
                  <User className="team-card__header-icon" />
                </div>

                <div className="team-card__body">
                  <div className={`team-card__avatar team-card__avatar--${founder.color}`}>
                    {founder.initial}
                  </div>
                  <h2 className="team-card__name">{founder.name}</h2>
                  <p className="team-card__role">{founder.role}</p>
                  <p className="team-card__bio">{founder.bio}</p>

                  <div className="team-card__socials">
                    {founder.socials.map((social) => (
                      <a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="team-card__social"
                        aria-label={`${founder.name}'s ${social.label}`}
                      >
                        <social.icon />
                      </a>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="section__title">Klar til et samarbejde?</h2>
            <p className="section__description">
              Lad os tage en snak om jeres projekt og finde ud af, hvordan vi kan hjælpe.
            </p>
            <div style={{ marginTop: 'var(--space-8)' }}>
              <Link to="/contact" className="btn btn--primary btn--large">
                Kontakt os
                <ArrowRight className="btn__icon" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default TeamPage;
